// iDenfy identity verification step for agent registration.
// The browser never decides the outcome: the iframe message only tells us to
// start checking, and the result comes from our server (iDenfy webhook).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ShieldCheck, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';

const IDENFY_ORIGIN = 'https://ui.idenfy.com';
const POLL_MS = 3000;
const SLOW_NOTICE_MS = 120000;
const FAILED = ['denied', 'expired', 'cancelled'];

const IdentityVerificationStep = ({ agencyCode, formData, verification, onChange, phoneVerification, onPhoneVerified, onContinue, onBack, canSkip, onSkip }) => {
  const [authToken, setAuthToken] = useState(null);
  const [starting, setStarting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState('');
  const finishedAt = useRef(null);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [dob, setDob] = useState('');
  const [legacyId, setLegacyId] = useState(null);
  const [code, setCode] = useState('');
  const [legacyBusy, setLegacyBusy] = useState(false);
  const [legacyMessage, setLegacyMessage] = useState('');
  const [legacyError, setLegacyError] = useState('');
  const [smsId, setSmsId] = useState(null);
  const [smsCode, setSmsCode] = useState('');
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsSentAt, setSmsSentAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const phoneOk = !!phoneVerification?.verified;
  const status = verification?.status || null;
  const passed = status === 'approved' || status === 'suspected';
  const verificationId = verification?.verificationId;

  useEffect(() => {
    if (!smsSentAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [smsSentAt]);

  const resendIn = Math.max(0, 30 - Math.floor((now - smsSentAt) / 1000));

  const sendSmsCode = async () => {
    setSmsBusy(true);
    setSmsError('');
    try {
      const { data } = await api.post('/registration/identity/phone/request', { agencyCode, email: formData.email, phone: formData.phone });
      setSmsId(data.phoneVerificationId);
      setSmsCode('');
      setSmsSentAt(Date.now());
      setNow(Date.now());
    } catch (err) {
      setSmsError(err.response?.data?.message || 'Unable to send a text message right now. Please try again.');
    } finally {
      setSmsBusy(false);
    }
  };

  const confirmSmsCode = async () => {
    setSmsBusy(true);
    setSmsError('');
    try {
      await api.post('/registration/identity/phone/verify', { phoneVerificationId: smsId, code: smsCode });
      onPhoneVerified({ phoneVerificationId: smsId, verified: true, phone: formData.phone, email: formData.email });
    } catch (err) {
      setSmsError(err.response?.data?.message || 'That code is not valid.');
    } finally {
      setSmsBusy(false);
    }
  };

  const start = async () => {
    setStarting(true);
    setError('');
    setFinished(false);
    setSlow(false);
    try {
      const { data } = await api.post('/registration/identity/session', {
        agencyCode,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        country: formData.country,
        phoneVerificationId: phoneVerification?.phoneVerificationId
      });
      setAuthToken(data.authToken || null);
      onChange({
        verificationId: data.verificationId,
        status: data.status,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to start identity verification right now. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const requestLegacyCode = async () => {
    setLegacyBusy(true);
    setLegacyError('');
    setLegacyMessage('');
    try {
      const { data } = await api.post('/registration/identity/legacy/request', {
        agencyCode,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        country: formData.country,
        dateOfBirth: dob,
        phoneVerificationId: phoneVerification?.phoneVerificationId
      });
      setLegacyId(data.verificationId);
      setCode('');
      setLegacyMessage(`If we found your earlier verification, we sent a 6-digit code to ${formData.email}. It is valid for ${data.codeValidMinutes} minutes. If nothing arrives, you can continue with ID verification below.`);
    } catch (err) {
      setLegacyError(err.response?.data?.message || 'Unable to check your earlier verification right now. Please continue with ID verification.');
    } finally {
      setLegacyBusy(false);
    }
  };

  const confirmLegacyCode = async () => {
    setLegacyBusy(true);
    setLegacyError('');
    try {
      const { data } = await api.post('/registration/identity/legacy/verify', { verificationId: legacyId, code });
      onChange({
        verificationId: data.verificationId,
        status: data.status,
        method: 'legacy',
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      });
    } catch (err) {
      setLegacyError(err.response?.data?.message || 'That code is not valid.');
    } finally {
      setLegacyBusy(false);
    }
  };

  const markFinished = useCallback(() => {
    finishedAt.current = finishedAt.current || Date.now();
    setFinished(true);
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== IDENFY_ORIGIN) return;
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== 'object') return;
      console.debug('[iDenfy] iframe message', { status: data.status, manualStatus: data.manualStatus });
      if (['approved', 'failed'].includes(String(data.status).toLowerCase())) markFinished();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [markFinished]);

  // Poll our own server while a session is open. `finished=1` lets the server
  // make a rate-limited direct check with iDenfy if the webhook is late.
  useEffect(() => {
    if (!authToken || !verificationId || passed || FAILED.includes(status)) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/registration/identity/${verificationId}/status`, { params: finished ? { finished: 1 } : {} });
        if (data.status !== status) onChange({ ...verification, status: data.status });
        if (finishedAt.current && Date.now() - finishedAt.current > SLOW_NOTICE_MS) setSlow(true);
      } catch (err) {
        // transient errors are retried on the next tick
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [authToken, verificationId, passed, status, finished, verification, onChange]);

  const showIframe = authToken && !passed && !FAILED.includes(status);
  const processing = authToken && finished && !passed && !FAILED.includes(status);

  return (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[var(--foreground)] mb-2 flex items-center">
          <ShieldCheck className="w-6 h-6 mr-2" />
          Verify Your Identity
        </h2>
        <p className="text-[var(--muted-foreground)]">
          To protect our agents and clients, we verify your identity before your application is reviewed.
          Have a government-issued photo ID (passport, driver&apos;s license or ID card) ready. You will also be asked to take a quick selfie.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start" role="alert">
          <AlertCircle className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {passed && (
        <div className="mb-6 p-4 rounded-md bg-green-50 border border-green-200 text-green-800 flex items-start" role="status">
          <CheckCircle2 className="w-5 h-5 mr-2 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{status === 'approved' ? 'Identity verified' : 'Verification received'}</p>
            {verification?.method === 'legacy' && (
              <p className="text-sm">We matched your earlier mstagents.com verification, so you do not need to verify again.</p>
            )}
            {status === 'suspected' && (
              <p className="text-sm">Your verification will be reviewed together with your application.</p>
            )}
          </div>
        </div>
      )}

      {processing && (
        <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200 text-blue-800 flex items-start" role="status">
          <Loader2 className="w-5 h-5 mr-2 mt-0.5 shrink-0 animate-spin" />
          <div>
            <p className="font-medium">Checking your verification...</p>
            <p className="text-sm">This usually takes less than a minute. Please keep this page open.</p>
            {slow && <p className="text-sm mt-1">This is taking longer than usual. Your result will appear here as soon as it is ready.</p>}
          </div>
        </div>
      )}

      {FAILED.includes(status) && (
        <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-200 text-red-700 flex items-start" role="alert">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">
              {status === 'denied' ? 'We could not verify your identity.' : 'Your verification session ended before it was completed.'}
            </p>
            <p className="text-sm">Please try again, making sure your ID is clear, fully visible and not expired.</p>
          </div>
        </div>
      )}

      {!phoneOk && (
        <div className="mb-6 border border-[var(--border)] rounded-md p-4 space-y-3">
          <h3 className="font-semibold text-[var(--foreground)]">Step 1: Verify your phone number</h3>
          <p className="text-sm text-[var(--muted-foreground)]">
            We will text a 4-digit code to <span className="font-medium text-[var(--foreground)]">{formData.phone}</span>. Need to change it? Go back and update your phone number.
          </p>
          {!smsId ? (
            <button
              type="button"
              onClick={sendSmsCode}
              disabled={smsBusy}
              className={`py-2 px-4 rounded-md text-white font-medium ${smsBusy ? 'bg-[var(--muted-foreground)] cursor-not-allowed' : 'bg-[var(--primary)] hover:brightness-110'}`}
            >
              {smsBusy ? 'Sending...' : 'Text Me a Code'}
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                placeholder="4-digit code"
                aria-label="Text message code"
                className="flex-1 px-3 py-2 border border-[var(--border)] rounded-md tracking-widest"
              />
              <button
                type="button"
                onClick={confirmSmsCode}
                disabled={smsCode.length !== 4 || smsBusy}
                className={`py-2 px-4 rounded-md text-white font-medium ${smsCode.length !== 4 || smsBusy ? 'bg-[var(--muted-foreground)] cursor-not-allowed' : 'bg-[var(--primary)] hover:brightness-110'}`}
              >
                {smsBusy ? 'Verifying...' : 'Verify Code'}
              </button>
              <button
                type="button"
                onClick={sendSmsCode}
                disabled={smsBusy || resendIn > 0}
                className="text-sm underline text-[var(--muted-foreground)] disabled:no-underline disabled:opacity-60"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
          )}
          {smsError && <p className="text-sm text-red-700" role="alert">{smsError}</p>}
        </div>
      )}

      {phoneOk && !passed && (
        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800 flex items-center" role="status">
          <CheckCircle2 className="w-4 h-4 mr-2 shrink-0" />
          Phone number verified. Now verify your identity.
        </div>
      )}

      {phoneOk && !passed && !authToken && (
        <div className="mb-6 border border-[var(--border)] rounded-md p-4">
          {!legacyOpen ? (
            <button type="button" onClick={() => setLegacyOpen(true)} className="text-sm font-medium text-[var(--primary)] underline">
              I already verified my identity on mstagents.com
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[var(--muted-foreground)]">
                Enter your date of birth as it appears on your ID. If we find your earlier verification, we will email a code to {formData.email} to confirm it is you.
              </p>
              {!legacyId ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    aria-label="Date of birth"
                    className="flex-1 px-3 py-2 border border-[var(--border)] rounded-md"
                  />
                  <button
                    type="button"
                    onClick={requestLegacyCode}
                    disabled={!dob || legacyBusy}
                    className={`py-2 px-4 rounded-md text-white font-medium ${!dob || legacyBusy ? 'bg-[var(--muted-foreground)] cursor-not-allowed' : 'bg-[var(--primary)] hover:brightness-110'}`}
                  >
                    {legacyBusy ? 'Checking...' : 'Send Code'}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit code"
                    aria-label="Verification code"
                    className="flex-1 px-3 py-2 border border-[var(--border)] rounded-md tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={confirmLegacyCode}
                    disabled={code.length !== 6 || legacyBusy}
                    className={`py-2 px-4 rounded-md text-white font-medium ${code.length !== 6 || legacyBusy ? 'bg-[var(--muted-foreground)] cursor-not-allowed' : 'bg-[var(--primary)] hover:brightness-110'}`}
                  >
                    {legacyBusy ? 'Verifying...' : 'Verify Code'}
                  </button>
                  <button type="button" onClick={() => { setLegacyId(null); setLegacyMessage(''); setLegacyError(''); }} className="text-sm underline text-[var(--muted-foreground)]">
                    Start over
                  </button>
                </div>
              )}
              {legacyMessage && <p className="text-sm text-[var(--foreground)]" role="status">{legacyMessage}</p>}
              {legacyError && <p className="text-sm text-red-700" role="alert">{legacyError}</p>}
            </div>
          )}
        </div>
      )}

      {showIframe && (
        <iframe
          title="Identity verification"
          src={`${IDENFY_ORIGIN}/?authToken=${encodeURIComponent(authToken)}&lang=en`}
          allow="camera; fullscreen; clipboard-write"
          allowFullScreen
          className="w-full mx-auto block border-0"
          style={{ maxWidth: 670, height: 800 }}
        />
      )}

      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-[var(--border)] rounded-md text-[var(--muted-foreground)] bg-white hover:bg-[var(--muted)] font-medium transition-colors order-2 sm:order-1"
        >
          Back
        </button>
        {passed ? (
          <button
            type="button"
            onClick={onContinue}
            className="flex-1 py-3 px-4 rounded-md text-white font-medium transition-colors order-1 sm:order-2 bg-[var(--primary)] hover:brightness-110"
          >
            Next: Complete Tax Form
          </button>
        ) : phoneOk && (!authToken || FAILED.includes(status)) && (
          <button
            type="button"
            onClick={start}
            disabled={starting}
            className={`flex-1 py-3 px-4 rounded-md text-white font-medium transition-colors order-1 sm:order-2 ${
              starting ? 'bg-[var(--muted-foreground)] cursor-not-allowed' : 'bg-[var(--primary)] hover:brightness-110'
            }`}
          >
            {starting ? 'Starting...' : FAILED.includes(status) ? 'Try Again' : 'Start Identity Verification'}
          </button>
        )}
      </div>

      {canSkip && !passed && (
        <div className="pt-4 text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-medium text-[var(--primary)] underline"
          >
            Skip identity verification for now (testing only)
          </button>
          <p className="text-xs text-[var(--muted-foreground)] mt-1">This option is only available on the staging site.</p>
        </div>
      )}
    </div>
  );
};

export default IdentityVerificationStep;
